import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationConfig, NavigationItem } from '@config/navigation';

interface CommandItem {
  id: string;
  label: string;
  path: string;
  keywords?: string[];
}

const buildCommands = (nav: NavigationItem[], locationPath: string): CommandItem[] => {
  const base: CommandItem[] = nav.map((n) => ({
    id: n.path,
    label: n.title,
    path: n.path,
    keywords: [n.title.toLowerCase()],
  }));

  // Contextual commands (breadcrumbs-like quick nav)
  if (locationPath !== '/') {
    base.unshift({ id: '/', label: 'Home', path: '/' });
  }

  // Quick actions
  base.push({ id: '/contact', label: 'Contact', path: '/contact' });

  return base;
};

const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      if ((isMac && e.metaKey && e.key.toLowerCase() === 'k') || (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (open && e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const commands = useMemo(() => buildCommands(navigationConfig, location.pathname), [location.pathname]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(q) || c.path.toLowerCase().includes(q) || (c.keywords || []).some(k => k.includes(q)));
  }, [commands, query]);

  const onSelect = (cmd: CommandItem) => {
    setOpen(false);
    navigate(cmd.path);
  };

  if (!open) return null;

  return (
    <div aria-hidden={!open} className="fixed inset-0 z-[100]">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />

      {/* Dialog */}
      <div role="dialog" aria-modal="true" aria-label="Command palette" className="absolute left-1/2 top-24 w-full max-w-xl -translate-x-1/2 rounded-apple bg-white shadow-apple">
        <div className="border-b border-secondary/20 p-3">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, e.g., About, Academy, Contact"
            aria-label="Search commands"
            className="w-full rounded-apple border border-secondary/20 px-3 py-2 outline-none focus:border-primary focus:ring-0"
          />
        </div>
        <ul role="listbox" className="max-h-80 overflow-auto py-1">
          {filtered.length === 0 && (
            <li className="px-4 py-2 text-sm text-secondary">No matches</li>
          )}
          {filtered.map((c) => (
            <li key={c.id} role="option">
              <button
                onClick={() => onSelect(c)}
                className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-secondary-dark hover:bg-secondary-light"
              >
                <span>{c.label}</span>
                <span className="text-xs text-secondary">{c.path}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-t border-secondary/20 px-4 py-2 text-xs text-secondary">
          <span>Navigate quickly</span>
          <span>
            <kbd className="rounded border px-1">⌘</kbd> + <kbd className="rounded border px-1">K</kbd>
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
